import { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import api, { fmtUsd, fmtPct, formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { TrendUp, TrendDown, Wallet, Copy, Lock, X } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

export default function PortfolioPage() {
  const { user } = useAuth();
  const [wallet, setWallet] = useState({ balance: 0, equity: 0, unrealized_pnl: 0, open_margin: 0, initial_balance: 0 });
  const [history, setHistory] = useState([]);
  const [positions, setPositions] = useState([]);

  const accountId = user?.telegram_id || user?.email || user?.id?.slice(0, 8);
  const accountSource = user?.telegram_id ? "Telegram ID" : "Email";
  const isWebUser = !user?.telegram_id;
  const [pwOpen, setPwOpen] = useState(false);

  const copyId = async () => {
    if (!accountId) return;
    try {
      await navigator.clipboard.writeText(accountId);
      toast.success("Copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const load = async () => {
    try {
      const [w, h, p] = await Promise.all([
        api.get("/wallet"),
        api.get("/trade/positions/history"),
        api.get("/trade/positions"),
      ]);
      setWallet(w.data);
      setHistory(h.data);
      setPositions(p.data);
    } catch {}
  };

  useEffect(() => {
    load();
    const i = setInterval(load, 1500);
    return () => clearInterval(i);
  }, []);

  const realized = history.reduce((a, t) => a + (t.pnl || 0), 0);
  const totalPnL = realized + wallet.unrealized_pnl;
  const pnlPct = wallet.initial_balance > 0 ? (totalPnL / wallet.initial_balance) * 100 : 0;
  const totalTrades = history.length;
  const wins = history.filter((t) => (t.pnl || 0) > 0).length;
  const losses = history.filter((t) => (t.pnl || 0) < 0).length;
  const winRate = (wins + losses) > 0 ? (wins / (wins + losses)) * 100 : 0;

  return (
    <div className="App flex flex-col min-h-screen">
      <Navbar />
      {pwOpen && <ChangePasswordModal onClose={() => setPwOpen(false)} />}
      <div className="flex-1 p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto w-full pb-32">
        <div className="flex items-center justify-between mb-5 sm:mb-8 gap-3 flex-wrap">
          <div className="min-w-0">
            <p className="overline">Portfolio</p>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-semibold mt-1">Trading Performance</h1>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {accountId && (
              <button
                onClick={copyId}
                data-testid="account-id-chip"
                className="flex items-center gap-2 px-3 py-2 bg-[#111827] border border-[#1F2937] hover:border-[#FCD535] rounded-sm transition-colors group shrink-0"
                title={`Your ${accountSource} — share with admin for deposits`}
              >
                <div className="text-left">
                  <div className="text-[9px] uppercase tracking-widest text-[#9CA3AF] leading-none">
                    Your {accountSource}
                  </div>
                  <div className="mono text-xs sm:text-sm text-white mt-0.5 truncate max-w-[150px] sm:max-w-none">
                    {accountId}
                  </div>
                </div>
                <Copy size={14} className="text-[#9CA3AF] group-hover:text-[#FCD535]" />
              </button>
            )}
            {isWebUser && (
              <button
                onClick={() => setPwOpen(true)}
                data-testid="change-password-btn"
                className="flex items-center gap-2 px-3 py-2 h-full bg-[#111827] border border-[#1F2937] hover:border-[#FCD535] rounded-sm transition-colors text-[#9CA3AF] hover:text-white shrink-0"
                title="Change password"
              >
                <Lock size={15} />
                <span className="text-xs hidden sm:inline">Change password</span>
              </button>
            )}
          </div>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#1F2937] mb-6 sm:mb-8">
          <StatCard label="Total Equity" value={`$${fmtUsd(wallet.equity)}`} accent="#FCD535" testId="stat-equity" />
          <StatCard label="Available" value={`$${fmtUsd(wallet.balance)}`} sub={`Margin: $${fmtUsd(wallet.open_margin)}`} testId="stat-available" />
          <StatCard
            label="Total PnL"
            value={`${totalPnL >= 0 ? "+" : ""}$${fmtUsd(totalPnL)}`}
            sub={fmtPct(pnlPct)}
            accent={totalPnL >= 0 ? "#0ECB81" : "#F6465D"}
            icon={totalPnL >= 0 ? <TrendUp size={16} weight="bold" /> : <TrendDown size={16} weight="bold" />}
            testId="stat-pnl"
          />
          <StatCard label="Win Rate" value={`${winRate.toFixed(0)}%`} sub={`${wins}W · ${losses}L · ${totalTrades}`} testId="stat-winrate" />
        </div>

        {/* Open positions */}
        <section className="mb-6 sm:mb-8">
          <p className="overline mb-3">Open Positions ({positions.length})</p>
          <div className="bg-[#111827] border border-[#1F2937] rounded-sm overflow-hidden">
            {positions.length === 0 ? (
              <div className="text-center py-8 text-sm text-[#9CA3AF]">No open positions</div>
            ) : (
              <>
                {/* Mobile cards */}
                <div className="lg:hidden divide-y divide-[#1F2937]">
                  {positions.map((p) => (
                    <div key={p.id} className="p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">{p.symbol.replace("USDT", "/USDT")}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-semibold ${p.side === "BUY" ? "bg-[#0ECB81]/15 text-[#0ECB81]" : "bg-[#F6465D]/15 text-[#F6465D]"}`}>
                            {p.side === "BUY" ? "LONG" : "SHORT"}
                          </span>
                          <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-[#FCD535]/15 text-[#FCD535] font-semibold mono">{p.leverage}x</span>
                        </div>
                        <div className={`mono font-semibold text-sm ${p.unrealized_pnl >= 0 ? "text-[#0ECB81]" : "text-[#F6465D]"}`}>
                          {p.unrealized_pnl >= 0 ? "+" : ""}${fmtUsd(p.unrealized_pnl)}
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-[11px] mono">
                        <div><div className="text-[9px] uppercase tracking-wider text-[#9CA3AF]">Qty</div>{p.quantity}</div>
                        <div><div className="text-[9px] uppercase tracking-wider text-[#9CA3AF]">Entry</div>${fmtUsd(p.entry_price)}</div>
                        <div><div className="text-[9px] uppercase tracking-wider text-[#9CA3AF]">Mark</div>${fmtUsd(p.mark_price)}</div>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Desktop table */}
                <table className="hidden lg:table w-full text-xs">
                  <thead className="text-[10px] uppercase tracking-widest text-[#9CA3AF] border-b border-[#1F2937]">
                    <tr>
                      <th className="text-left px-4 py-2.5">Symbol</th>
                      <th className="text-left">Side</th>
                      <th className="text-right">Qty</th>
                      <th className="text-right">Entry</th>
                      <th className="text-right">Mark</th>
                      <th className="text-right">Lev</th>
                      <th className="text-right px-4">Unrealized PnL</th></tr>
                  </thead>
                  <tbody className="mono">
                    {positions.map((p) => (
                      <tr key={p.id} className="border-b border-[#1F2937] last:border-0">
                        <td className="px-4 py-2.5 font-semibold">{p.symbol.replace("USDT", "/USDT")}</td>
                        <td className={p.side === "BUY" ? "text-[#0ECB81]" : "text-[#F6465D]"}>{p.side === "BUY" ? "LONG" : "SHORT"}</td>
                        <td className="text-right">{p.quantity}</td>
                        <td className="text-right">{fmtUsd(p.entry_price)}</td>
                        <td className="text-right">{fmtUsd(p.mark_price)}</td>
                        <td className="text-right text-[#FCD535]">{p.leverage}x</td>
                        <td className={`text-right px-4 font-semibold ${p.unrealized_pnl >= 0 ? "text-[#0ECB81]" : "text-[#F6465D]"}`}>
                          {p.unrealized_pnl >= 0 ? "+" : ""}${fmtUsd(p.unrealized_pnl)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
        </section>

        {/* History — closed positions */}
        <section>
          <p className="overline mb-3">Closed Positions ({history.length})</p>
          <div className="bg-[#111827] border border-[#1F2937] rounded-sm overflow-hidden">
            {history.length === 0 ? (
              <div className="text-center py-8 text-sm text-[#9CA3AF]">No closed positions yet</div>
            ) : (
              <div className="max-h-[60vh] lg:max-h-[520px] overflow-y-auto" data-testid="closed-positions-scroll">
                {/* Mobile cards */}
                <div className="lg:hidden divide-y divide-[#1F2937]">
                  {history.map((p) => {
                    const profit = (p.pnl || 0) >= 0;
                    const pnlPct = p.margin ? (p.pnl / p.margin) * 100 : 0;
                    return (
                      <div key={p.id} className="p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold">{p.symbol.replace("USDT", "/USDT")}</span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-semibold ${p.side === "BUY" ? "bg-[#0ECB81]/15 text-[#0ECB81]" : "bg-[#F6465D]/15 text-[#F6465D]"}`}>
                              {p.side === "BUY" ? "LONG" : "SHORT"}
                            </span>
                            <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-[#FCD535]/15 text-[#FCD535] font-semibold mono">{p.leverage}x</span>
                          </div>
                          <div className={`mono font-semibold text-sm ${profit ? "text-[#0ECB81]" : "text-[#F6465D]"}`}>
                            {profit ? "+" : ""}${fmtUsd(p.pnl)} <span className="text-[10px]">({fmtPct(pnlPct)})</span>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-[11px] mono">
                          <div><div className="text-[9px] uppercase tracking-wider text-[#9CA3AF]">Size</div>{p.quantity}</div>
                          <div><div className="text-[9px] uppercase tracking-wider text-[#9CA3AF]">Entry</div>${fmtUsd(p.entry_price)}</div>
                          <div><div className="text-[9px] uppercase tracking-wider text-[#9CA3AF]">Exit</div>${fmtUsd(p.exit_price || 0)}</div>
                        </div>
                        <div className="text-[10px] text-[#9CA3AF] mono pt-1">
                          {new Date(p.closed_at).toLocaleString()}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {/* Desktop table */}
                <table className="hidden lg:table w-full text-xs">
                  <thead className="text-[10px] uppercase tracking-widest text-[#9CA3AF] sticky top-0 z-10 bg-[#111827]">
                    <tr className="border-b border-[#1F2937]">
                      <th className="text-left px-4 py-2.5">Closed</th>
                      <th className="text-left">Symbol</th>
                      <th className="text-left">Side</th>
                      <th className="text-right">Qty</th>
                      <th className="text-right">Entry</th>
                      <th className="text-right">Exit</th>
                      <th className="text-right">Lev</th>
                      <th className="text-right px-4">PnL</th>
                    </tr>
                  </thead>
                  <tbody className="mono">
                    {history.map((p) => {
                      const profit = (p.pnl || 0) >= 0;
                      return (
                        <tr key={p.id} className="border-b border-[#1F2937] last:border-0">
                          <td className="px-4 py-2.5 text-[#9CA3AF]">{new Date(p.closed_at).toLocaleString()}</td>
                          <td className="font-semibold">{p.symbol.replace("USDT", "/USDT")}</td>
                          <td className={p.side === "BUY" ? "text-[#0ECB81]" : "text-[#F6465D]"}>{p.side === "BUY" ? "LONG" : "SHORT"}</td>
                          <td className="text-right">{p.quantity}</td>
                          <td className="text-right">{fmtUsd(p.entry_price)}</td>
                          <td className="text-right">{fmtUsd(p.exit_price || 0)}</td>
                          <td className="text-right text-[#FCD535]">{p.leverage}x</td>
                          <td className={`text-right px-4 font-semibold ${profit ? "text-[#0ECB81]" : "text-[#F6465D]"}`}>
                            {profit ? "+" : ""}${fmtUsd(p.pnl)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, accent = "#F9FAFB", icon, testId }) {
  return (
    <div className="bg-[#111827] p-3 sm:p-5 min-w-0" data-testid={testId}>
      <div className="flex items-center justify-between mb-1.5 sm:mb-2">
        <p className="overline truncate">{label}</p>
        {icon && <span style={{ color: accent }} className="shrink-0">{icon}</span>}
      </div>
      <p className="text-lg sm:text-xl lg:text-2xl mono font-semibold truncate" style={{ color: accent }}>{value}</p>
      {sub && <p className="text-[10px] sm:text-xs text-[#9CA3AF] mt-1 mono truncate">{sub}</p>}
    </div>
  );
}

function ChangePasswordModal({ onClose }) {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (next !== confirm) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success("Password changed");
      onClose();
    } catch (err) { toast.error(formatErr(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      onClick={() => !busy && onClose()} data-testid="change-password-backdrop">
      <div className="bg-[#0A0E17] border border-[#1F2937] rounded-sm w-full max-w-sm p-5"
        onClick={(e) => e.stopPropagation()} data-testid="change-password-modal">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Lock size={16} className="text-[#FCD535]" />
            <h3 className="text-lg font-semibold">Change password</h3>
          </div>
          <button onClick={onClose} disabled={busy} className="text-[#9CA3AF] hover:text-white" data-testid="close-change-password">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <input type="password" value={cur} onChange={(e) => setCur(e.target.value)} required autoComplete="current-password"
            placeholder="Current password"
            className="w-full h-10 px-3 bg-[#111827] border border-[#1F2937] focus:border-[#FCD535] outline-none rounded-sm text-sm"
            data-testid="cp-current-input" />
          <input type="password" value={next} onChange={(e) => setNext(e.target.value)} required minLength={6} autoComplete="new-password"
            placeholder="New password (min 6 chars)"
            className="w-full h-10 px-3 bg-[#111827] border border-[#1F2937] focus:border-[#FCD535] outline-none rounded-sm text-sm"
            data-testid="cp-new-input" />
          <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required autoComplete="new-password"
            placeholder="Confirm new password"
            className="w-full h-10 px-3 bg-[#111827] border border-[#1F2937] focus:border-[#FCD535] outline-none rounded-sm text-sm"
            data-testid="cp-confirm-input" />
          <Button type="submit" disabled={busy}
            className="w-full h-10 bg-[#FCD535] hover:bg-[#FCD535]/90 text-black font-semibold rounded-sm"
            data-testid="cp-submit">
            {busy ? "Saving…" : "Save"}
          </Button>
        </form>
      </div>
    </div>
  );
}
